import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { apiFetch, API_BASE } from "./utils/apiClient";
import {
  DRAWING_TYPE_TEXT,
  BOARD_TYPE_TEXT,
  createProjectItem,
  computeProjectResults,
  buildInvoiceItemsFromProjectRows,
} from "./utils/projectCalc";

// 🧾 نفس مجموعة INVOICE_PROPOSAL_USERS بالضبط من App.js / invoiceRoutes.js -
// فقط هذي الأسماء مسموح لها فعلياً بإنشاء فاتورة (POST /invoices) بالباك اند،
// فنعرض زر "إرسال إلى الفواتير" فقط لهن حتى لا يصطدم بقية المستخدمين بخطأ 403
const INVOICE_PROPOSAL_USERS = [
  "admin",
  "Baneen@123",
  "rand@123",
  "maryam@123",
  "farah@123",
  "maysa@123",
];

// 📐 بيانات قائمة الأسعار + معادلات سعر اللوحة/البيع/المشروع صارت بملف
// مشترك (utils/projectCalc.js) - نفس الأرقام حرفياً، تستخدمها أيضاً "فاتورة
// المشروع" بـInvoicePage.jsx.

// حقول ما تقبل قيم سالبة
const NON_NEGATIVE_FIELDS = ["lengthCm", "widthCm", "manualBoardPrice", "manualSalePrice"];

export default function ProjectCostCalculator() {
  const navigate = useNavigate();
  const [page, setPage] = useState("new");
  const [searchTerm, setSearchTerm] = useState("");
  const [savedProjects, setSavedProjects] = useState([]);
  const [editingProjectId, setEditingProjectId] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [projectLocation, setProjectLocation] = useState("");
  const [managerName, setManagerName] = useState("");
  const [managerPosition, setManagerPosition] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [exportingPdf, setExportingPdf] = useState(false);
  const [sendingToInvoice, setSendingToInvoice] = useState(false);
  const pdfContentRef = useRef(null);
  const addRowBtnRef = useRef(null);

  const currentUsername =
    typeof window !== "undefined" ? localStorage.getItem("username") : null;
  const canSendToInvoice = INVOICE_PROPOSAL_USERS.includes(currentUsername);
  // 🧾 المشروع المفتوح حالياً إذا مربوط بفاتورة (أُرسل للفواتير، أو أنشأته
  // بنين كفاتورة مشروع) - بدل زر الإرسال يظهر رابط الفاتورة
  const editingProject = savedProjects.find((p) => p.id === editingProjectId);

const createItem = (index = 1) => createProjectItem(index);

  const [items, setItems] = useState([createItem(1)]);

  useEffect(() => {
    loadSavedData();
  }, []);

  const loadSavedData = async () => {
  const res = await apiFetch(`${API_BASE}/projects`);
  const data = await res.json();

  if (!res.ok) {
    console.error("Error loading projects:", data.error);
    return;
  }

  setSavedProjects(data);
};


  const fmtIQD = (n) => {
    const value = Number(n);
    return Number.isFinite(value) ? Math.round(value).toLocaleString() : "0";
  };


  /* ======================================================
     ديفولت السعر القديم عند عدم إدخال الطول والعرض
     هذا حتى يطلع مثل القديم:
     إطار أيل + داخلي = 50,000
     إطار كاس = 58,000
     خشب داخلي فقط = 28,000
  ====================================================== */
  const getDefaultBoardPrice = (it) => {
    if (it.boardType === "print_only") return 28000;
    if (it.boardType === "print_kas") return 58000;
    return 50000;
  };
const formatProjectDate = (dateValue) => {
  if (!dateValue) return "";

  let date;

  if (dateValue?.toDate) {
    date = dateValue.toDate();
  } else {
    date = new Date(dateValue);
  }

  if (Number.isNaN(date.getTime())) return "";

  return date.toLocaleString("ar-IQ", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

  const updateItem = (idx, field, value) => {
    // الطول/العرض/سعر اللوحة/سعر البيع ما يقبلون سالب
    const safeValue = NON_NEGATIVE_FIELDS.includes(field)
      ? String(value).replace(/-/g, "")
      : value;
    const updated = [...items];
    updated[idx] = {
      ...updated[idx],
      [field]: safeValue,
    };
    setItems(updated);
  };

  // ⏎ Enter بجدول التفاصيل ينقل للحقل اللي بعده (نفس ترتيب الأعمدة)، وآخر
  // حقل ينقل لزر "سطر جديد" (Enter ثاني يضيف سطر)
  const handleTableKeyDown = (e) => {
    const el = e.target;
    if (e.key === "-" && el.dataset.nonNegative) {
      e.preventDefault();
      return;
    }
    if (e.key !== "Enter" || !["INPUT", "SELECT"].includes(el.tagName)) return;
    e.preventDefault();
    const fields = Array.from(
      e.currentTarget.querySelectorAll("input, select")
    );
    const next = fields[fields.indexOf(el) + 1];
    if (next) {
      next.focus();
      if (next.tagName === "INPUT") next.select();
    } else {
      addRowBtnRef.current?.focus();
    }
  };

  const addNewRow = () => {
    setItems([...items, createItem(items.length + 1)]);
  };

  const deleteRow = (idx) => {
    const updated = items
      .filter((_, i) => i !== idx)
      .map((item, i) => ({
        ...item,
        name: i + 1,
      }));

    setItems(updated.length ? updated : [createItem(1)]);
  };

  const results = useMemo(() => computeProjectResults(items), [items]);


    const currentTotals = {
    totalBoardPrice: results.rows.reduce(
      (sum, row) => sum + Number(row.boardTotal || 0),
      0
    ),

    totalSalePrice: results.rows.reduce(
      (sum, row) => sum + Number(row.saleTotal || 0),
      0
    ),

    totalProject: results.rows.reduce(
      (sum, row) => sum + Number(row.projectTotal || 0),
      0
    ),
  };
const saveProject = async () => {
  if (!projectName) {
    alert("يرجى إدخال اسم المشروع أولاً");
    return;
  }

  const projectData = {
    projectName,
    projectLocation,
    managerName,
    managerPosition,
    mobileNumber,
    items,
    results,
  };

  if (editingProjectId) {
    const res = await apiFetch(`${API_BASE}/projects/${editingProjectId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(projectData),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      alert(data.error || "حدث خطأ أثناء حفظ التعديل");
      return;
    }

    await loadSavedData();

    alert("✅ تم حفظ التعديل");
    setPage("home");
    setEditingProjectId(null);
    return;
  }

  const res = await apiFetch(`${API_BASE}/projects`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(projectData),
  });

  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    alert(data.error || "حدث خطأ أثناء حفظ المشروع");
    return;
  }

  await loadSavedData();

  alert("✅ تم حفظ المشروع");
  setPage("home");
  setEditingProjectId(null);
};

// 🧾 يحوّل بنود المشروع (الطول/العرض/نوع اللوحة/نوع الإطار...) إلى شكل
// بنود الفاتورة (panelType/frameOption/frameType/sizeKey...) - الحقول
// التصميمية غير الموجودة أصلاً بحساب المشروع (شكل اللوحة الدقيق، لون
// الإطار، الصور) تُترك فارغة عمداً لتكملها الموظفة يدوياً بعد فتح الفاتورة،
// بينما القياسات والكمية والسعر النهائي تُنقَل حرفياً من نتائج الحساب حتى
// يطابق مجموع الفاتورة مجموع المشروع تماماً.
const buildInvoiceItemsFromProject = () =>
  buildInvoiceItemsFromProjectRows(results.rows);

// 🧾 إرسال المشروع الحالي إلى واجهة الفواتير: ينشئ فاتورة جديدة غير معتمدة
// (نفس POST /invoices المستخدم فعلياً بصفحة الفاتورة - رقم الفاتورة يتولد
// تلقائياً بالسيرفر بنفس تسلسل باقي الفواتير) محمّلة ببنود وأسعار المشروع،
// حتى تفتحها الموظفة من واجهة الفواتير وتكمل التفاصيل الناقصة (المحافظة،
// أقرب نقطة دالة، صور اللوحات...) قبل الاعتماد
const sendProjectToInvoice = async () => {
  if (!projectName) {
    alert("يرجى إدخال اسم المشروع أولاً");
    return;
  }

  if (!results.rows.length) {
    alert("يرجى إضافة بند واحد على الأقل قبل الإرسال إلى الفواتير");
    return;
  }

  if (
    !window.confirm(
      "سيتم إنشاء فاتورة مشروع جديدة غير معتمدة من بيانات هذا المشروع (تظهر عند بنين بواجهة الفواتير كمشروع)، وتقدرين تكملين تفاصيلها من واجهة الفواتير. متابعة؟"
    )
  ) {
    return;
  }

  const invoiceItems = buildInvoiceItemsFromProject();
  const subtotal = invoiceItems.reduce(
    (sum, it) => sum + Number(it.total || 0),
    0
  );
  const totalPanels = invoiceItems.reduce(
    (sum, it) => sum + Number(it.quantity || 0),
    0
  );

  // 💰 نفس معادلات الديفولت والحساب التلقائي المستخدمة لأي فاتورة جديدة
  // بصفحة الفاتورة (recalcInvoice بـInvoicePage.jsx) - حتى تكون الفاتورة
  // الناتجة "حالها حال البقية" فور فتحها، وليست بأرقام صفرية غريبة
  const consultationFee = 75000;
  const siteVisitFee = 75000;
  const installationFee =
    totalPanels > 1
      ? 25000 + (totalPanels - 1) * 5000
      : totalPanels > 0
      ? 25000
      : 0;
  const transportFee =
    totalPanels > 1
      ? 10000 + (totalPanels - 1) * 5000
      : totalPanels > 0
      ? 10000
      : 0;
  const discount = 0;
  const finalTotal =
    subtotal +
    consultationFee +
    installationFee +
    transportFee +
    siteVisitFee -
    discount;

  const invoicePayload = {
    customerName: managerName || projectName,
    phone: mobileNumber || "",
    projectName,
    companyName: "",
    governorate: "",
    nearestPoint: projectLocation || "",
    date: new Date().toISOString().substring(0, 10),
    items: invoiceItems,
    subtotal,
    consultationFee,
    installationFee,
    transportFee,
    siteVisitFee,
    hideTransportFeeFromCustomer: false,
    notes: `تم إنشاؤها تلقائياً من حساب كلفة المشروع "${projectName}"`,
    discount,
    finalTotal,
    paymentMethod: "cod",
    advancePayment: 0,
    remainingPayment: finalTotal,
    saleAccount: "بيع",
    imageUrl: "",
    pdfUrl: "",
    // 🏗️ علامة "مشروع" - حتى واجهة الفواتير تفرزها بفلتر فواتير/مشاريع
    invoiceSource: "projectCost",
    // 📐 ربط الفاتورة بهذا المشروع + بنوده الخام، حتى تنفتح الفاتورة بجدول
    // المشروع (الموظفة تشوف سعر البيع والمجموع فقط) وأي نسخة جديدة منها
    // تحدّث المشروع هنا
    projectId: editingProjectId,
    projectItems: items,
    projectResults: results,
  };

  setSendingToInvoice(true);

  try {
    const res = await apiFetch(`${API_BASE}/invoices`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(invoicePayload),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      alert(data.error || "حدث خطأ أثناء إرسال المشروع إلى الفواتير");
      return;
    }

    alert(
      `✅ تم إنشاء الفاتورة رقم ${data.invoiceNumber} (غير معتمدة) - افتحيها الآن لإكمال التفاصيل الناقصة`
    );
    navigate(`/invoice/edit/${data.id}`);
  } catch (error) {
    console.error("Error sending project to invoice:", error);
    alert("حدث خطأ أثناء إرسال المشروع إلى الفواتير");
  } finally {
    setSendingToInvoice(false);
  }
};

 const handleDeleteProject = async (id, e) => {
  e.stopPropagation();

  if (window.confirm("❗ هل تريد حذف المشروع؟")) {
    await apiFetch(`${API_BASE}/projects/${id}`, { method: "DELETE" });
    await loadSavedData();
  }
};
// 📄 تصدير المشروع كملف PDF - نفس تقنية html2canvas + jsPDF المعتمدة أصلاً
// بالفواتير/العروض، مع تقطيع الصورة عبر عدة صفحات إذا الجدول طويل
const exportProjectPdf = async () => {
  if (!pdfContentRef.current) return;

  setExportingPdf(true);
  // ننتظر إعادة الرسم حتى تتحول حقول الإدخال إلى نص عادي (وتختفي أزرار
  // الحذف/الإضافة) بالمحتوى المصوَّر - قبل ما نلتقط الصورة
  await new Promise((resolve) => setTimeout(resolve, 100));

  try {
    const canvas = await html2canvas(pdfContentRef.current, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
    });

    const pdf = new jsPDF("l", "mm", "a4");
    const pageWidthMm = 297;
    const pageHeightMm = 210;

    const imgWidth = pageWidthMm;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const imgData = canvas.toDataURL("image/png");

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeightMm;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeightMm;
    }

    const safeName = (projectName || "مشروع").replace(/[\\/:*?"<>|]/g, "-");
    pdf.save(`${safeName}.pdf`);
  } finally {
    setExportingPdf(false);
  }
};

const getProjectTotals = (project) => {
  const rows = project.results?.rows || [];

  const totalBoardPrice = rows.reduce(
    (sum, row) => sum + Number(row.boardTotal || 0),
    0
  );

  const totalSalePrice = rows.reduce(
    (sum, row) => sum + Number(row.saleTotal || 0),
    0
  );

  const totalProject = rows.reduce(
    (sum, row) => sum + Number(row.projectTotal || 0),
    0
  );

  return {
    totalBoardPrice,
    totalSalePrice,
    totalProject:
      totalProject || Number(project.results?.grandTotal || 0),
  };
};
  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerTitle}>🎨 نظام حساب المشروع</div>

        {page !== "home" && (
          <button onClick={() => setPage("home")} style={styles.backBtn}>
            الرئيسية ↩
          </button>
        )}
      </header>

      {page === "home" ? (
        <div style={styles.card}>
          <div style={styles.searchBarRow}>
            <input
              style={styles.input}
              placeholder="🔍 ابحث..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />

            <button
              onClick={() => {
            setPage("new");
setEditingProjectId(null);
setProjectName("");
setProjectLocation("");
setManagerName("");
setManagerPosition("");
setMobileNumber("");
setItems([createItem(1)]);
              }}
              style={styles.addBtn}
            >
              ➕ مشروع جديد
            </button>
          </div>

          <div style={styles.projectGrid}>
            {savedProjects
              .filter((p) =>
                p.projectName
                  ?.toLowerCase()
                  .includes(searchTerm.toLowerCase())
              )
              .map((p) => (
                <div
                  key={p.id}
                  style={styles.projectItem}
                  onClick={() => {
                  setEditingProjectId(p.id);
setProjectName(p.projectName || "");
setProjectLocation(p.projectLocation || "");
setManagerName(p.managerName || "");
setManagerPosition(p.managerPosition || "");
setMobileNumber(p.mobileNumber || "");

                    const loadedItems =
                      p.items && p.items.length
                        ? p.items.map((item, i) => ({
                            ...createItem(i + 1),
                            ...item,
                            name: item.name || i + 1,
                          }))
                        : [createItem(1)];

                    setItems(loadedItems);
                    setPage("view");
                  }}
                >
                <div style={{ flex: 1 }}>
  <div style={styles.projectName}>{p.projectName}</div>
  <div style={styles.projectManager}>{p.managerName}</div>
  {p.invoiceId && (
    <div style={styles.projectBadges}>
      {p.createdByUsername && p.createdByUsername !== "admin" && (
        <span style={styles.badgePurple}>
          🏗️ من {p.createdByUsername === "Baneen@123" ? "بنين" : p.createdByUsername}
        </span>
      )}
      <span style={styles.badgeBlue}>🧾 فاتورة {p.invoiceNumber}</span>
      <span style={p.approved ? styles.badgeGreen : styles.badgeYellow}>
        {p.approved ? "✅ معتمد" : "⏳ غير معتمد"}
      </span>
    </div>
  )}

  <div style={styles.projectDate}>
    التاريخ: {formatProjectDate(p.createdAt)}
  </div>
</div>
{(() => {
  const totals = getProjectTotals(p);

  return (
    <div style={styles.projectTotalsBox}>
      <div style={styles.totalMiniBox}>
        <div style={styles.smallText}>مجموع سعر اللوحة</div>
        <div style={styles.savedTotalBlue}>
          IQD {fmtIQD(totals.totalBoardPrice)}
        </div>
      </div>

      <div style={styles.totalMiniBox}>
        <div style={styles.smallText}>مجموع سعر البيع</div>
        <div style={styles.savedTotalOrange}>
          IQD {fmtIQD(totals.totalSalePrice)}
        </div>
      </div>

      <div style={styles.totalMiniBox}>
        <div style={styles.smallText}>مجموع المشروع</div>
        <div style={styles.savedTotal}>
          IQD {fmtIQD(totals.totalProject)}
        </div>
      </div>
    </div>
  );
})()}

                  <button
                    onClick={(e) => handleDeleteProject(p.id, e)}
                    style={styles.listDelBtn}
                  >
                    🗑️
                  </button>
                </div>
              ))}
          </div>
        </div>
      ) : (
        <div>
          <div ref={pdfContentRef}>
          <div style={styles.card}>
            <h3 style={styles.sectionTitle}>📋 بيانات المشروع</h3>

            <div style={styles.gridFields}>
              {exportingPdf ? (
                <div style={styles.printField}>{projectName || "—"}</div>
              ) : (
                <input
                  style={styles.input}
                  placeholder="اسم المشروع"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                />
              )}

              {exportingPdf ? (
                <div style={styles.printField}>{projectLocation || "—"}</div>
              ) : (
                <input
                  style={styles.input}
                  placeholder="موقع المشروع"
                  value={projectLocation}
                  onChange={(e) => setProjectLocation(e.target.value)}
                />
              )}

              {exportingPdf ? (
                <div style={styles.printField}>{managerName || "—"}</div>
              ) : (
                <input
                  style={styles.input}
                  placeholder="المسؤول"
                  value={managerName}
                  onChange={(e) => setManagerName(e.target.value)}
                />
              )}

              {exportingPdf ? (
                <div style={styles.printField}>{managerPosition || "—"}</div>
              ) : (
                <input
                  style={styles.input}
                  placeholder="منصب المسؤول"
                  value={managerPosition}
                  onChange={(e) => setManagerPosition(e.target.value)}
                />
              )}

              {exportingPdf ? (
                <div style={styles.printField}>{mobileNumber || "—"}</div>
              ) : (
                <input
                  style={styles.input}
                  placeholder="الهاتف"
                  value={mobileNumber}
                  inputMode="numeric"
                  onChange={(e) => setMobileNumber(e.target.value.replace(/[^0-9]/g, ""))}
                />
              )}
            </div>
          </div>

          <div style={styles.card}>
            <h3 style={styles.sectionTitle}>📐 التفاصيل</h3>

            <div style={styles.itemsWrapper}>
              <div style={styles.excelTable} onKeyDown={handleTableKeyDown}>
                <div style={styles.excelHeader}>
                  <div style={styles.thSmall}>الطول</div>
                  <div style={styles.thSmall}>العرض</div>
                  <div style={styles.thBoardType}>نوع اللوحة</div>
                  <div style={styles.thMedium}>نوع الإطار</div>
                  <div style={styles.thQty}>العدد</div>
                  {!exportingPdf && <div style={styles.thPriceSmall}>سعر اللوحة</div>}
                  {!exportingPdf && <div style={styles.thPriceSmall}>المجموع</div>}
                  <div style={styles.thPriceSmall}>سعر البيع</div>
                  {!exportingPdf && <div style={styles.thPriceSmall}>المجموع</div>}
                  {!exportingPdf && <div style={styles.thValue}>ادخل القيمة</div>}
                  {!exportingPdf && <div style={styles.thPriceSmall}>سعر المشروع</div>}
                  <div style={styles.thPriceSmall}>المجموع</div>
                  {!exportingPdf && <div style={styles.thDelete}>حذف</div>}
                </div>

                {items.map((it, idx) => {
                  const res = results.rows[idx];

                  return (
                    <div key={idx} style={styles.excelRow}>
                      <div style={styles.tdSmall}>
                        {exportingPdf ? (
                          <div style={styles.printCell}>{it.lengthCm}</div>
                        ) : (
                          <input
                            style={styles.smallInput}
                            type="number"
                            min="0"
                            data-non-negative="1"
                            value={it.lengthCm}
                            onChange={(e) =>
                              updateItem(idx, "lengthCm", e.target.value)
                            }
                          />
                        )}
                      </div>

                      <div style={styles.tdSmall}>
                        {exportingPdf ? (
                          <div style={styles.printCell}>{it.widthCm}</div>
                        ) : (
                          <input
                            style={styles.smallInput}
                            type="number"
                            min="0"
                            data-non-negative="1"
                            value={it.widthCm}
                            onChange={(e) =>
                              updateItem(idx, "widthCm", e.target.value)
                            }
                          />
                        )}
                      </div>

                      <div style={styles.tdBoardType}>
                        {exportingPdf ? (
                          <div style={styles.printCell}>{DRAWING_TYPE_TEXT[it.drawingType]}</div>
                        ) : (
                          <select
                            style={styles.selectSmall}
                            value={it.drawingType}
                            onChange={(e) =>
                              updateItem(idx, "drawingType", e.target.value)
                            }
                          >
                            <option value="origin">اورجن</option>
                            <option value="edition">ادشن</option>
                          </select>
                        )}
                      </div>

                      <div style={styles.tdMedium}>
                        {exportingPdf ? (
                          <div style={styles.printCell}>{BOARD_TYPE_TEXT[it.boardType]}</div>
                        ) : (
                          <select
                            style={styles.select}
                            value={it.boardType}
                            onChange={(e) =>
                              updateItem(idx, "boardType", e.target.value)
                            }
                          >
                            <option value="print_only">خشب داخلي فقط</option>
                            <option value="print_ail">إطار أيل + داخلي</option>
                            <option value="print_kas">إطار كاس</option>
                          </select>
                        )}
                      </div>

                      <div style={styles.tdQty}>
                        {exportingPdf ? (
                          <div style={styles.printCell}>{it.qty}</div>
                        ) : (
                          <input
                            style={styles.qtyInput}
                            type="number"
                            min="1"
                            value={it.qty}
                            onChange={(e) =>
                              updateItem(idx, "qty", e.target.value)
                            }
                          />
                        )}
                      </div>

                      {!exportingPdf && (
                        <div style={styles.tdPriceSmall}>
                          <input
                            style={styles.priceInputSmall}
                            type="number"
                            min="0"
                            data-non-negative="1"
                            value={
                              it.manualBoardPrice !== ""
                                ? it.manualBoardPrice
                                : Math.round(res?.boardPrice || 0)
                            }
                            onChange={(e) =>
                              updateItem(idx, "manualBoardPrice", e.target.value)
                            }
                          />
                        </div>
                      )}

                      {!exportingPdf && (
                        <div style={styles.tdResultSmall}>
                          {fmtIQD(res?.boardTotal)}
                        </div>
                      )}

                      <div style={styles.tdPriceSmall}>
                        {exportingPdf ? (
                          <div style={styles.printCell}>
                            {fmtIQD(
                              it.manualSalePrice !== ""
                                ? it.manualSalePrice
                                : res?.salePrice
                            )}
                          </div>
                        ) : (
                          <input
                            style={styles.priceInputSmall}
                            type="number"
                            min="0"
                            data-non-negative="1"
                            value={
                              it.manualSalePrice !== ""
                                ? it.manualSalePrice
                                : Math.round(res?.salePrice || 0)
                            }
                            onChange={(e) =>
                              updateItem(idx, "manualSalePrice", e.target.value)
                            }
                          />
                        )}
                      </div>

                      {!exportingPdf && (
                        <div style={styles.tdResultSmall}>
                          {fmtIQD(res?.saleTotal)}
                        </div>
                      )}

                      {!exportingPdf && (
                        <div style={styles.tdValue}>
                          <input
                            style={styles.valueInput}
                            type="number"
                            step="0.001"
                            value={it.customValue}
                            onChange={(e) =>
                              updateItem(idx, "customValue", e.target.value)
                            }
                          />
                        </div>
                      )}

                      {!exportingPdf && (
                        <div style={styles.tdResultSmall}>
                          {fmtIQD(res?.projectPrice)}
                        </div>
                      )}

                      <div style={styles.tdResultGreenSmall}>
                        {fmtIQD(res?.projectTotal)}
                      </div>

                      {!exportingPdf && (
                        <div style={styles.tdDelete}>
                          <button
                            onClick={() => deleteRow(idx)}
                            style={styles.delBtn}
                          >
                            🗑️
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {!exportingPdf && (
              <button ref={addRowBtnRef} onClick={addNewRow} style={styles.addMoreBtn}>
                + سطر جديد
              </button>
            )}
          </div>

        <div style={styles.footer}>
  <div style={styles.footerTotals}>
    {!exportingPdf && (
      <div style={styles.totalLineBlue}>
        إجمالي سعر اللوحة: IQD {fmtIQD(currentTotals.totalBoardPrice)}
      </div>
    )}

    <div style={styles.totalLineOrange}>
      إجمالي سعر البيع: IQD {fmtIQD(currentTotals.totalSalePrice)}
    </div>

    <div style={styles.grandTotal}>
      إجمالي سعر المشروع: IQD {fmtIQD(currentTotals.totalProject)}
    </div>
  </div>

  {!exportingPdf && (
    <div style={styles.footerButtons}>
      <button onClick={exportProjectPdf} style={styles.pdfBtn}>
        📄 تصدير PDF
      </button>
      <button onClick={saveProject} style={styles.saveBtn}>
        {editingProjectId ? "حفظ التعديل 💾" : "حفظ 💾"}
      </button>
      {editingProject?.invoiceId && (
        <button
          onClick={() => navigate(`/invoice/${editingProject.invoiceId}`)}
          style={styles.invoiceBtn}
        >
          🧾 فتح الفاتورة {editingProject.invoiceNumber}
        </button>
      )}
      {editingProjectId && !editingProject?.invoiceId && canSendToInvoice && (
        <button
          onClick={sendProjectToInvoice}
          style={styles.invoiceBtn}
          disabled={sendingToInvoice}
        >
          {sendingToInvoice ? "جارٍ الإرسال..." : "🧾 إرسال إلى الفواتير"}
        </button>
      )}
    </div>
  )}
</div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  // الصفحة تطلع من حدود الـContainer العام (1200px) وتاخذ عرض الشاشة (لحد
  // 1600px) حتى كل أعمدة الجدول تبين بسطر واحد بدون سكرول جانبي
  container: {
    width: "min(1600px, calc(100vw - 48px))",
    marginTop: "10px",
    marginBottom: "10px",
    marginLeft: "calc((100% - min(1600px, calc(100vw - 48px))) / 2)",
    marginRight: "calc((100% - min(1600px, calc(100vw - 48px))) / 2)",
    boxSizing: "border-box",
    direction: "rtl",
    fontFamily: "sans-serif",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "15px 0",
    borderBottom: "2px solid #eee",
  },

  headerTitle: {
    fontSize: "22px",
    fontWeight: "bold",
  },

  card: {
    background: "#fff",
    padding: "15px",
    borderRadius: "12px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    marginTop: "20px",
  },

  sectionTitle: {
    color: "#1976d2",
    borderRight: "4px solid #1976d2",
    paddingRight: "10px",
    fontSize: "16px",
  },

  gridFields: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "15px",
  },

  input: {
    padding: "10px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    fontSize: "14px",
    width: "100%",
    boxSizing: "border-box",
  },

  // 🖨️ نص عادي بديل عن input فقط عند تصدير PDF (نفس مقاس/حدود input تقريباً
  // حتى ما يتحرك التصميم)
  printField: {
    padding: "10px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "bold",
    width: "100%",
    boxSizing: "border-box",
    textAlign: "center",
  },

  printCell: {
    fontWeight: "bold",
    fontSize: "13px",
  },

  searchBarRow: {
    display: "flex",
    gap: "10px",
    marginBottom: "20px",
  },

  addBtn: {
    background: "#1976d2",
    color: "#fff",
    border: "none",
    padding: "10px 20px",
    borderRadius: "8px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  backBtn: {
    background: "#f5f5f5",
    border: "1px solid #ddd",
    padding: "5px 15px",
    borderRadius: "8px",
    cursor: "pointer",
  },

  projectGrid: {
    display: "grid",
    gap: "10px",
  },

  projectItem: {
    padding: "15px",
    border: "1px solid #eee",
    borderRadius: "8px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
  },

  projectName: {
    fontWeight: "bold",
  },

  projectManager: {
    fontSize: "12px",
    color: "#888",
  },

  smallText: {
    fontSize: "11px",
    color: "#666",
  },

  savedTotal: {
    fontWeight: "bold",
    color: "#2e7d32",
  },

  listDelBtn: {
    background: "#fff1f1",
    border: "1px solid #ffcdd2",
    padding: "5px 8px",
    borderRadius: "6px",
    color: "#d32f2f",
    cursor: "pointer",
  },

  itemsWrapper: {
    overflowX: "auto",
  },

  excelTable: {
    minWidth: "1280px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    overflow: "hidden",
  },

  excelHeader: {
    display: "flex",
    background: "#f5f5f5",
    fontWeight: "bold",
    borderBottom: "1px solid #ddd",
  },

  excelRow: {
    display: "flex",
    borderBottom: "1px solid #eee",
    background: "#fff",
  },

  thValue: {
    width: "84px",
    padding: "10px 4px",
    textAlign: "center",
    borderLeft: "1px solid #ddd",
    fontSize: "11px",
    boxSizing: "border-box",
  },

  thBoardType: {
    width: "76px",
    padding: "10px 4px",
    textAlign: "center",
    borderLeft: "1px solid #ddd",
    fontSize: "11px",
    boxSizing: "border-box",
  },

  thQty: {
    width: "84px",
    padding: "10px 4px",
    textAlign: "center",
    borderLeft: "1px solid #ddd",
    fontSize: "11px",
    boxSizing: "border-box",
  },

  thSmall: {
    width: "72px",
    padding: "10px 4px",
    textAlign: "center",
    borderLeft: "1px solid #ddd",
    fontSize: "11px",
    boxSizing: "border-box",
  },

  thMedium: {
    width: "120px",
    padding: "10px 4px",
    textAlign: "center",
    borderLeft: "1px solid #ddd",
    fontSize: "11px",
    boxSizing: "border-box",
  },

  thPriceSmall: {
    width: "105px",
    padding: "10px 4px",
    textAlign: "center",
    borderLeft: "1px solid #ddd",
    fontSize: "11px",
    boxSizing: "border-box",
  },

  thDelete: {
    width: "48px",
    padding: "10px 4px",
    textAlign: "center",
    fontSize: "11px",
    boxSizing: "border-box",
  },

  tdValue: {
    width: "84px",
    padding: "6px 4px",
    textAlign: "center",
    borderLeft: "1px solid #eee",
    boxSizing: "border-box",
  },

  tdBoardType: {
    width: "76px",
    padding: "6px 4px",
    textAlign: "center",
    borderLeft: "1px solid #eee",
    boxSizing: "border-box",
  },

  tdQty: {
    width: "84px",
    padding: "6px 4px",
    textAlign: "center",
    borderLeft: "1px solid #eee",
    boxSizing: "border-box",
  },

  tdSmall: {
    width: "72px",
    padding: "6px 4px",
    textAlign: "center",
    borderLeft: "1px solid #eee",
    boxSizing: "border-box",
  },

  tdMedium: {
    width: "120px",
    padding: "6px 4px",
    textAlign: "center",
    borderLeft: "1px solid #eee",
    boxSizing: "border-box",
  },

  tdPriceSmall: {
    width: "105px",
    padding: "6px 4px",
    textAlign: "center",
    borderLeft: "1px solid #eee",
    boxSizing: "border-box",
  },

  tdResultSmall: {
    width: "105px",
    padding: "12px 4px",
    textAlign: "center",
    borderLeft: "1px solid #eee",
    boxSizing: "border-box",
    fontWeight: "bold",
    background: "#fafafa",
    fontSize: "13px",
  },

  tdResultGreenSmall: {
    width: "105px",
    padding: "12px 4px",
    textAlign: "center",
    borderLeft: "1px solid #eee",
    boxSizing: "border-box",
    fontWeight: "bold",
    background: "#e8f5e9",
    color: "#2e7d32",
    fontSize: "13px",
  },

  tdDelete: {
    width: "48px",
    padding: "6px 4px",
    textAlign: "center",
    boxSizing: "border-box",
  },

  smallInput: {
    width: "56px",
    padding: "7px 4px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    textAlign: "center",
    boxSizing: "border-box",
  },

  qtyInput: {
    width: "64px",
    padding: "7px 4px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    textAlign: "center",
    boxSizing: "border-box",
  },

  valueInput: {
    width: "68px",
    padding: "7px 4px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    textAlign: "center",
    boxSizing: "border-box",
  },

  priceInputSmall: {
    width: "82px",
    padding: "7px 4px",
    border: "1px solid #1976d2",
    borderRadius: "6px",
    textAlign: "center",
    fontWeight: "bold",
    boxSizing: "border-box",
  },

  select: {
    width: "108px",
    padding: "7px 4px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    boxSizing: "border-box",
  },

  selectSmall: {
    width: "68px",
    padding: "7px 4px",
    border: "1px solid #ddd",
    borderRadius: "6px",
    boxSizing: "border-box",
  },

  delBtn: {
    background: "#fff1f1",
    border: "1px solid #ffcdd2",
    padding: "6px",
    borderRadius: "6px",
    color: "#d32f2f",
    cursor: "pointer",
  },

  addMoreBtn: {
    width: "100%",
    padding: "12px",
    border: "1px dashed #1976d2",
    background: "none",
    color: "#1976d2",
    fontWeight: "bold",
    cursor: "pointer",
    borderRadius: "8px",
    marginTop: "12px",
  },

  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "20px",
    padding: "20px",
    background: "#fff",
    borderRadius: "12px",
  },

  footerButtons: {
    display: "flex",
    gap: "10px",
    alignItems: "center",
  },

  pdfBtn: {
    padding: "10px 24px",
    background: "#fff",
    color: "#1976d2",
    border: "2px solid #1976d2",
    borderRadius: "8px",
    fontWeight: "bold",
    cursor: "pointer",
  },

  grandTotal: {
    fontSize: "24px",
    fontWeight: "bold",
    color: "#2e7d32",
  },

  saveBtn: {
    padding: "10px 40px",
    background: "#2e7d32",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontWeight: "bold",
    cursor: "pointer",
  },
  invoiceBtn: {
    padding: "10px 24px",
    background: "#ef6c00",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontWeight: "bold",
    cursor: "pointer",
  },
  projectTotalsBox: {
  display: "flex",
  gap: "14px",
  alignItems: "center",
  justifyContent: "flex-end",
  flexWrap: "wrap",
},

projectBadges: {
  display: "flex",
  gap: "6px",
  flexWrap: "wrap",
  marginTop: "6px",
},

badgePurple: {
  fontSize: "11px",
  fontWeight: "bold",
  padding: "3px 8px",
  borderRadius: "20px",
  background: "#ede7f6",
  color: "#4527a0",
},

badgeBlue: {
  fontSize: "11px",
  fontWeight: "bold",
  padding: "3px 8px",
  borderRadius: "20px",
  background: "#e3f2fd",
  color: "#0d47a1",
},

badgeGreen: {
  fontSize: "11px",
  fontWeight: "bold",
  padding: "3px 8px",
  borderRadius: "20px",
  background: "#d4edda",
  color: "#155724",
},

badgeYellow: {
  fontSize: "11px",
  fontWeight: "bold",
  padding: "3px 8px",
  borderRadius: "20px",
  background: "#fff3cd",
  color: "#856404",
},

totalMiniBox: {
  minWidth: "120px",
  textAlign: "center",
},

savedTotalBlue: {
  fontWeight: "bold",
  color: "#1976d2",
  fontSize: "15px",
},

savedTotalOrange: {
  fontWeight: "bold",
  color: "#ef6c00",
  fontSize: "15px",
},
projectDate: {
  marginTop: "6px",
  fontSize: "12px",
  color: "#777",
  fontWeight: "600",
},
footerTotals: {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  alignItems: "flex-start",
},

totalLineBlue: {
  fontSize: "18px",
  fontWeight: "bold",
  color: "#1976d2",
},

totalLineOrange: {
  fontSize: "18px",
  fontWeight: "bold",
  color: "#ef6c00",
},
};